import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService, claimPendingReferral } from '../services/api';
import { CheckCircle2, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';
import { tokenIdentityProvider } from '../utils/authToken';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const code = searchParams.get('code');
    if (!code) {
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;

    authService.oauthCallback(code, redirectUri)
      .then(async (res) => {
        if (res.data?.success) {
          await login(res.data.data);
          void claimPendingReferral();
          toast.success('Signed in with LinkedIn!');
          // LinkedIn sign-ins go straight into the prefilled resume builder
          // (Workspace imports the identity claims when no profile exists yet).
          const provider = tokenIdentityProvider(res.data.data.access_token);
          navigate(provider === 'linkedin' ? '/workspace' : '/dashboard', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.error || 'Failed to sign in with LinkedIn';
        toast.error(msg);
        navigate('/', { replace: true });
      });
  }, []);

  const isActionRedirect = !searchParams.get('code');

  if (isActionRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page px-4">
        <div className="w-full max-w-md bg-white rounded-card border border-zinc-200 shadow-card p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-brand-50 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-brand-500" />
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground mb-2">
            Email verified!
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            Your email address has been verified. You can now log in to your account.
          </p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="inline-flex h-11 px-6 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-colors duration-200 items-center justify-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            Log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="w-8 h-8 border-[3px] border-zinc-200 border-t-brand-500 rounded-full animate-spin" />
    </div>
  );
};

export default AuthCallback;
